import { createGeoJsonAdapter as createGeoJsonAdapter_ } from '@nextgis/ngw-kit';
import { createExpressionCallback, preparePaint } from '@nextgis/paint';

import { createModel3DAdapter } from './createModel3DAdapter';

import type { Expression } from '@nextgis/expression';
import type { FeatureLayerField } from '@nextgis/ngw-connector';
import type { GetClassAdapterOptions } from '@nextgis/ngw-kit';
import type { Ellipsoid3DPaint, VectorAdapterLayerPaint } from '@nextgis/paint';
import type { Type } from '@nextgis/utils';
import type {
  AdapterOptions,
  GeoJsonAdapterOptions,
  MainLayerAdapter,
} from '@nextgis/webmap';
import type { CompositeRead } from '@nextgisweb/resource/type/api';
import type { Style3DRead } from '@nextgisweb/style-3d/type/api';
import type { Feature } from 'geojson';

type Style3DFieldType = Style3DRead['poi_color_type'];
type StyleType = Style3DRead['style_type'];

export interface Style3DPayload {
  type: StyleType;
  zOffset: number;
  MODEL: Style3DModel;
  GEOJSON: GEOJSON;
  POI: POI;
  SPHERE: SPHERE;
  ELLIPSOID: ELLIPSOID;
}

interface POI {
  color: Style3DFieldDefinition;
  priority: Priority;
  limit: Style3DRead['poi_limit'];
  icon: Style3DFieldDefinition;
}

interface Priority {
  field: Style3DRead['poi_priority_field'];
  inverse: Style3DRead['poi_priority_inverse'];
}

interface GEOJSON {
  stroke: Style3DFieldDefinition;
  strokeWidth: Style3DFieldDefinition;
  fill: Style3DFieldDefinition;
  height: Style3DFieldDefinition;
}

interface SPHERE {
  stroke: Style3DFieldDefinition;
  strokeWidth: Style3DFieldDefinition;
  fill: Style3DFieldDefinition;
  radius: Style3DFieldDefinition;
}

interface ELLIPSOID {
  stroke: Style3DFieldDefinition;
  strokeWidth: Style3DFieldDefinition;
  fill: Style3DFieldDefinition;
  length: Style3DFieldDefinition;
  width: Style3DFieldDefinition;
  height: Style3DFieldDefinition;
}

export interface Style3DFieldDefinition<T = any> {
  field?: number | null;
  type: Style3DFieldType;
  value: T;
}

export interface Style3DModel {
  id: Style3DFieldDefinition;
  scale: Style3DFieldDefinition;
  rotate: Style3DFieldDefinition;
}

export type Style3D = Style3DPayload | Style3DRead;

export function val3DToExpression<T = any>(
  val: Style3DFieldDefinition<T>,
  fields: FeatureLayerField[],
): T | Expression {
  const type = val.type;
  const fieldId = val.field;
  if (fieldId && type === 'FIELD') {
    const field = fields.find((x) => x.id === Number(fieldId));
    if (field) {
      return ['get', field.keyname];
    }
  }
  return val.value;
}

export function prepareStyle(style3D: Style3D): Style3D {
  let s = style3D;
  const sp = {} as Style3DPayload;
  if ('style_type' in s) {
    sp.type = s.style_type;
    if (sp.type === 'POI') {
      sp.POI = {
        color: {
          field: s.poi_color_field,
          type: s.poi_color_type,
          value: s.poi_color,
        },
        icon: {
          field: s.poi_icon_field,
          type: s.poi_icon_type,
          value: s.poi_icon,
        },
        limit: s.poi_limit,
        priority: {
          field: s.poi_priority_field,
          inverse: s.poi_priority_inverse,
        },
      };
    } else if (sp.type === 'GEOJSON') {
      sp.GEOJSON = {
        fill: {
          field: s.gj_stroke_fill_field,
          type: s.gj_stroke_fill_type,
          value: s.gj_stroke_fill,
        },
        height: {
          field: s.gj_height_field,
          type: s.gj_height_type,
          value: s.gj_height,
        },
        stroke: {
          field: s.gj_stroke_field,
          type: s.gj_stroke_type,
          value: s.gj_stroke,
        },
        strokeWidth: {
          field: s.gj_stroke_width_field,
          type: s.gj_stroke_width_type,
          value: s.gj_stroke_width,
        },
      };
    } else if (sp.type === 'SPHERE') {
      sp.SPHERE = {
        fill: {
          field: s.sphere_stroke_fill_field,
          type: s.sphere_stroke_fill_type,
          value: s.sphere_stroke_fill,
        },
        stroke: {
          field: s.sphere_stroke_field,
          type: s.sphere_stroke_type,
          value: s.sphere_stroke,
        },
        strokeWidth: {
          field: s.sphere_stroke_width_field,
          type: s.sphere_stroke_width_type,
          value: s.sphere_stroke_width,
        },
        radius: {
          field: s.sphere_radius_field,
          type: s.sphere_radius_type,
          value: s.sphere_radius,
        },
      };
    } else if (sp.type === 'ELLIPSOID') {
      sp.ELLIPSOID = {
        fill: {
          field: s.ellipsoid_stroke_fill_field,
          type: s.ellipsoid_stroke_fill_type,
          value: s.ellipsoid_stroke_fill,
        },
        stroke: {
          field: s.ellipsoid_stroke_field,
          type: s.ellipsoid_stroke_type,
          value: s.ellipsoid_stroke,
        },
        strokeWidth: {
          field: s.ellipsoid_stroke_width_field,
          type: s.ellipsoid_stroke_width_type,
          value: s.ellipsoid_stroke_width,
        },
        length: {
          field: s.ellipsoid_length_field,
          type: s.ellipsoid_length_type,
          value: s.ellipsoid_length,
        },
        width: {
          field: s.ellipsoid_width_field,
          type: s.ellipsoid_width_type,
          value: s.ellipsoid_width,
        },
        height: {
          field: s.ellipsoid_width_field,
          type: s.ellipsoid_width_type,
          value: s.ellipsoid_width,
        },
      };
    } else if (sp.type === 'MODEL') {
      sp.MODEL = {
        id: {
          field: s.model_id_field,
          type: s.model_id_type,
          value: s.model_id,
        },
        scale: {
          field: s.model_scale_field,
          type: s.model_scale_type,
          value: s.model_scale,
        },
        rotate: {
          field: s.model_rotate_field,
          type: s.model_rotate_type,
          value: s.model_rotate,
        },
      };
    }
    s = sp;
  }
  return s;
}

export function convertStyle3DToAdapterOptions(
  style: Style3DPayload,
  vectorItem: CompositeRead,
  adapterOptions?: Record<string, any>,
): GeoJsonAdapterOptions<Feature, any> | undefined {
  const fields = vectorItem.feature_layer?.fields;
  const popupOptions = adapterOptions?.popupOptions ?? {
    fromProperties: true,
  };
  if (fields) {
    if (style.type === 'POI') {
      const poi = style.POI;
      let orderBy;
      if (poi.priority && poi.priority.field) {
        const field = fields.find((x) => x.id === Number(poi.priority.field));
        if (field) {
          orderBy = [(poi.priority.inverse ? '-' : '') + field.keyname];
        }
      }
      const paint = preparePaint({
        paint: {
          type: 'pin',
          color: val3DToExpression(poi.color, fields),
          icon: val3DToExpression(poi.icon, fields),
        },
      });
      const poiStyle: GeoJsonAdapterOptions = {
        limit: poi.limit !== null ? poi.limit : undefined,
        orderBy,
        popupOptions,
        strategy: 'BBOX',
        paint,
      };
      return poiStyle;
    } else if (style.type === 'ELLIPSOID') {
      const e = style.ELLIPSOID;

      const strokeColor = val3DToExpression(e.stroke, fields);
      const fillColor = val3DToExpression(e.fill, fields);
      const strokeWidth = val3DToExpression(e.strokeWidth, fields);
      const length = val3DToExpression(e.length, fields);
      const width = val3DToExpression(e.width, fields);
      const height = val3DToExpression(e.height, fields);

      const layerPaint: Ellipsoid3DPaint = {
        type: 'ellipsoid',
        length,
        width,
        height,
      };
      if (strokeColor) {
        layerPaint.strokeColor = strokeColor;
      }
      if (fillColor) {
        layerPaint.fillColor = fillColor;
      }
      if (strokeWidth) {
        layerPaint.strokeWidth = strokeWidth;
      }
      const paint = createExpressionCallback(layerPaint);
      return {
        popupOptions,
        //@ts-ignore
        paint,
      };
    } else if (style.type === 'GEOJSON') {
      const g = style.GEOJSON;
      const layerPaint: VectorAdapterLayerPaint = {};
      const fillColor = val3DToExpression(g.fill, fields);
      if (fillColor) {
        layerPaint.fillColor = fillColor;
      }
      const strokeColor = val3DToExpression(g.stroke, fields);
      if (strokeColor) {
        layerPaint.strokeColor = strokeColor;
      }

      let strokeWidth = g.strokeWidth;
      if (strokeWidth === undefined) {
        // @ts-ignore
        strokeWidth = g.width;
        console.warn(
          'Parameter `width` is deprecated for type `GEOJSON`, use `strokeWidth` instead',
        );
      }
      if (strokeWidth !== undefined) {
        const weight = val3DToExpression(strokeWidth, fields);
        if (weight) {
          layerPaint.weight = Number(weight);
        }
      }
      const extrude3d = val3DToExpression<number>(g.height, fields);
      if (extrude3d) {
        layerPaint.extrude3d = extrude3d;
      }
      const paint = preparePaint({ paint: layerPaint });
      return {
        popupOptions,
        paint,
      };
    }
  }
}

export async function createStyle3DAdapter(
  options: GetClassAdapterOptions,
): Promise<Type<MainLayerAdapter<any, any, AdapterOptions>> | undefined> {
  const parent = options.item.resource.parent;
  if (!parent) {
    return;
  }

  const vectorItem = await options.connector.getResource(parent.id);
  if (vectorItem) {
    const style3d = options.item.style_3d;
    if (style3d) {
      return createGeoJsonAdapter(vectorItem, style3d, options);
    }
  }
}

export async function createGeoJsonAdapter(
  vectorItem: CompositeRead,
  style3d: Style3D,
  options: GetClassAdapterOptions,
): Promise<Type<MainLayerAdapter<any, any, AdapterOptions>> | undefined> {
  const s = prepareStyle(style3d);
  if ('type' in s) {
    if (s.type === 'MODEL') {
      return createModel3DAdapter(vectorItem, s.MODEL, options);
    }

    const { adapterOptions, ...layerOptions } = options.layerOptions;
    const style3DAdapterOptions = convertStyle3DToAdapterOptions(
      s,
      vectorItem,
      adapterOptions,
    );
    if (style3DAdapterOptions) {
      style3DAdapterOptions.props = { item: vectorItem };
      if ('z_offset' in style3d) {
        style3DAdapterOptions.heightOffset =
          style3d.z_offset !== null ? style3d.z_offset : undefined;
      } else if ('zOffset' in style3d) {
        style3DAdapterOptions.heightOffset =
          style3d.zOffset !== null ? (style3d.zOffset as number) : undefined;
      }
    }

    const geojsonOptions: GetClassAdapterOptions = {
      ...options,
      layerOptions: {
        ...layerOptions,
        adapterOptions: style3DAdapterOptions,
        resource: vectorItem.resource.id,
      },
      item: vectorItem,
      addLayerOptionsPriority: false,
    };

    return createGeoJsonAdapter_(geojsonOptions);
  }
}
